import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-2a831a65"),n=n(),_popScopeId(),n)
const _hoisted_1 = /*#__PURE__*/_createTextVNode("Loading tabulated association data")
const _hoisted_2 = /*#__PURE__*/_createTextVNode("Error loading tabulated association data")
const _hoisted_3 = { class: "truncate" }

import { ref, computed, watch, onMounted } from "vue";
import AppTable from "@/components/AppTable.vue";
import { Col, Cols, Sort } from "@/components/AppTable";
import AppNodeBadge from "@/components/AppNodeBadge.vue";
import AppRelationBadge from "@/components/AppRelationBadge.vue";
import { Node } from "@/api/node-lookup";
import { getTabulatedAssociations, Association } from "@/api/node-associations";
import { downloadJson } from "@/util/download";
import { snackbar } from "@/components/TheSnackbar";
import { Filters, filtersToQuery } from "@/api/facets";
import { Options } from "@/components/AppSelectMulti";
import { useQuery } from "@/util/composables";

interface Props {
  /** current node */
  node: Node;
  /** selected association category */
  selectedCategory: string;
  /** selected association id */
  selectedAssociation?: Association;
}

interface Emits {
  /** change selected association */
  (event: "select", value?: Association): void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AssociationsTable',
  props: {
    node: null,
    selectedCategory: null,
    selectedAssociation: null
  },
  emits: ["select"],
  setup(__props: any, { emit }: { emit: ({
  /** change selected association */
  (event: "select", value?: Association): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  /** current node */
  node: Node;
  /** selected association category */
  selectedCategory: string;
  /** selected association id */
  selectedAssociation?: Association;
}





/** table state */
const sort = ref<Sort>();
const perPage = ref(5);
const start = ref(0);
const search = ref("");
const availableFilters = ref<Filters>({});
const activeFilters = ref<Filters>({});

/** table columns */
const cols = computed((): Cols => {
  /** standard columns, always present */
  const baseCols: Cols = [
    {
      id: "subject",
      key: "subject",
      heading: props.node.category,
      width: "max-content",
      sortable: true,
    },
    {
      id: "relation",
      key: "relation",
      heading: "Association",
      width: "max-content",
      sortable: true,
    },
    {
      id: "object",
      key: "object",
      heading: props.selectedCategory,
      width: "max-content",
      sortable: true,
    },
    {
      id: "evidence",
      key: "supportCount",
      heading: "Evidence",
      width: "min-content",
      align: "center",
    },
  ];

  /** extra, supplemental columns for certain association types */
  const extraCols: Cols = [];

  /** taxon column. exists for many categories, so just add if any row has taxon. */
  if (associations.value.associations.some((association) => association.taxon))
    extraCols.push({
      id: "taxon",
      key: "taxon",
      heading: "Taxon",
      width: "max-content",
    });

  /** phenotype specific columns */
  if (props.selectedCategory === "phenotype") {
    extraCols.push(
      {
        id: "frequency",
        key: "frequency",
        heading: "Frequency",
        sortable: true,
      },
      {
        id: "onset",
        key: "onset",
        heading: "Onset",
        sortable: true,
      }
    );
  }

  /** publication specific columns */
  if (props.selectedCategory === "publication")
    extraCols.push(
      {
        id: "author",
        key: "author",
        heading: "Author",
        width: "max-content",
      },
      {
        id: "year",
        key: "year",
        heading: "Year",
        align: "center",
        width: "max-content",
      },
      {
        id: "publisher",
        key: "publisher",
        heading: "Publisher",
        width: "max-content",
      }
    );

  /**
   * filter out extra columns with nothing in them (all rows for that col
   * falsey)
   */
  /*
  extraCols = extraCols.filter((col) =>
    associations.value.some((association) => association[col.key || ""])
  );
  */

  /** put divider to separate base cols from extra cols */
  if (extraCols[0]) extraCols.unshift({ id: "divider" });

  return [...baseCols, ...extraCols];
});

/** when user changes active filters */
function onFilterChange(colId: Col["id"], value: Options) {
  if (activeFilters.value && activeFilters.value[colId]) {
    activeFilters.value[colId] = value;
    getAssociations(false);
  }
}

/** get table association data */
const {
  query: getAssociations,
  data: associations,
  isLoading,
  isError,
} = useQuery(
  async function (
    /**
     * whether to perform "fresh" search, without filters/pagination/etc. true
     * when search text changes, false when filters/pagination/etc change.
     */
    fresh: boolean
  ) {
    /** catch case where no association categories available */
    if (!props.node.associationCounts.length)
      throw new Error("No association info available");

    /** get association data */
    const response = await getTabulatedAssociations(
      props.node.id,
      props.node.category,
      props.selectedCategory,
      perPage.value,
      start.value,
      search.value,
      sort.value,
      fresh ? undefined : filtersToQuery(availableFilters.value),
      fresh ? undefined : filtersToQuery(activeFilters.value)
    );

    return response;
  },

  /** default value */
  { count: 0, associations: [], facets: {} },

  /** on success */
  (response, [fresh]) => {
    /** update filters from facets returned from api, if a "fresh" search */
    if (fresh) {
      availableFilters.value = { ...response.facets };
      activeFilters.value = { ...response.facets };
    }
  }
);

/** download table data */
async function download() {
  /** max rows to try to query */
  const max = 100000;

  /** warn user */
  snackbar(
    `Downloading data for ${Math.min(
      associations.value.count,
      max
    )} table entries.` +
      (associations.value.count >= 100 ? " This may take a minute." : "")
  );

  /** attempt to request all rows */
  const response = await getTabulatedAssociations(
    props.node.id,
    props.node.category,
    props.selectedCategory,
    max,
    0
  );
  downloadJson(response);
}

/** get associations when category or table state changes */
watch(
  () => props.selectedCategory,
  async () => await getAssociations(true)
);
watch([perPage, start, search, sort], async () => await getAssociations(false));

/** get associations on load */
onMounted(() => getAssociations(true));

return (_ctx: any,_cache: any) => {
  const _component_AppStatus = _resolveComponent("AppStatus")!
  const _component_AppButton = _resolveComponent("AppButton")!
  const _component_AppLink = _resolveComponent("AppLink")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_unref(isLoading))
    ? (_openBlock(), _createBlock(_component_AppStatus, {
        key: 0,
        code: "loading"
      }, {
        default: _withCtx(() => [
          _hoisted_1
        ]),
        _: 1
      }))
    : (_unref(isError))
      ? (_openBlock(), _createBlock(_component_AppStatus, {
          key: 1,
          code: "error"
        }, {
          default: _withCtx(() => [
            _hoisted_2
          ]),
          _: 1
        }))
      : (_openBlock(), _createBlock(AppTable, {
          key: 2,
          "per-page": perPage.value,
          "onUpdate:per-page": _cache[0] || (_cache[0] = ($event: any) => ((perPage).value = $event)),
          start: start.value,
          "onUpdate:start": _cache[1] || (_cache[1] = ($event: any) => ((start).value = $event)),
          search: search.value,
          "onUpdate:search": _cache[2] || (_cache[2] = ($event: any) => ((search).value = $event)),
          cols: _unref(cols),
          rows: _unref(associations).associations,
          total: _unref(associations).count,
          sort: sort.value,
          "available-filters": availableFilters.value,
          "active-filters": activeFilters.value,
          onDownload: download,
          onSort: _cache[3] || (_cache[3] = (value) => (sort.value = value)),
          onFilter: onFilterChange
        }, {
          subject: _withCtx(({ cell }) => [
            _createVNode(AppNodeBadge, {
              node: cell,
              link: false
            }, null, 8, ["node"])
          ]),
          relation: _withCtx(({ cell }) => [
            _createVNode(AppRelationBadge, { relation: cell }, null, 8, ["relation"])
          ]),
          object: _withCtx(({ cell, row }) => [
            _createVNode(AppNodeBadge, {
              node: cell,
              breadcrumb: { node: __props.node, relation: row.relation }
            }, null, 8, ["node", "breadcrumb"])
          ]),
          evidence: _withCtx(({ cell, row }) => [
            _withDirectives(_createVNode(_component_AppButton, {
              class: "evidence",
              text: String(cell),
              "aria-pressed": row.id === __props.selectedAssociation?.id,
              icon: row.id === __props.selectedAssociation?.id ? 'check' : 'flask',
              color: row.id === __props.selectedAssociation?.id ? 'primary' : 'secondary',
              onClick: ($event: any) => (
          emit('select', row.id === __props.selectedAssociation?.id ? undefined : row)
        )
            }, null, 8, ["text", "aria-pressed", "icon", "color", "onClick"]), [
              [_directive_tooltip, 
          row.id === __props.selectedAssociation?.id
            ? 'Viewing supporting evidence. Click again to hide.'
            : 'View supporting evidence for this association'
        ]
            ])
          ]),
          taxon: _withCtx(({ cell }) => [
            _createElementVNode("span", _hoisted_3, _toDisplayString(cell?.name), 1)
          ]),
          frequency: _withCtx(({ cell }) => [
            cell
              ? (_openBlock(), _createBlock(_component_AppLink, {
                  key: 0,
                  class: "truncate",
                  to: cell?.link,
                  "no-icon": true
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(cell?.name), 1)
                  ]),
                  _: 2
                }, 1032, ["to"]))
              : _createCommentVNode("", true)
          ]),
          onset: _withCtx(({ cell }) => [
            cell
              ? (_openBlock(), _createBlock(_component_AppLink, {
                  key: 0,
                  class: "truncate",
                  to: cell?.link,
                  "no-icon": true
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(cell?.name), 1)
                  ]),
                  _: 2
                }, 1032, ["to"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["per-page", "start", "search", "cols", "rows", "total", "sort", "available-filters", "active-filters"]))
}
}

})